import { createMap, parseMap, slugify, type MapDocument } from './MapDocument';

/** One saved draft, as the picker needs it. */
export interface DraftSummary {
  id: string;
  name: string;
  updated: number;
}

const STORAGE_KEY = 'magnetic-hound.maps.v1';
/** Where a map is parked across the reload that starts a test run. */
export const SESSION_KEY = 'magnetic-hound.map-test.v1';

function safeStorage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    const storage = kind === 'localStorage' ? localStorage : sessionStorage;
    if (!storage) return null;
    const probe = '__mh_map_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Draft storage for the map builder.
 *
 * Drafts live in `localStorage` so a layout survives a reload; the map being
 * tested is parked in `sessionStorage`, which is the hand-off the reload-based
 * test run uses (the same shape as the level select's URL navigation).
 */
export class MapStore {
  constructor(
    private readonly storage: Storage | null = safeStorage('localStorage'),
    private readonly session: Storage | null = safeStorage('sessionStorage'),
  ) {}

  private read(): Record<string, MapDocument> {
    if (!this.storage) return {};
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const drafts: Record<string, MapDocument> = {};
      for (const [id, value] of Object.entries(parsed)) {
        const map = parseMap(value);
        if (map) drafts[id] = map;
      }
      return drafts;
    } catch {
      return {};
    }
  }

  private write(drafts: Record<string, MapDocument>): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    } catch {
      // Storage may be unavailable; the map simply does not persist.
    }
  }

  /** Every saved draft, newest first. */
  list(): DraftSummary[] {
    const drafts = this.read();
    const updated = this.readTimes();
    return Object.entries(drafts)
      .map(([id, map]) => ({ id, name: map.name, updated: updated[id] ?? 0 }))
      .sort((a, b) => b.updated - a.updated);
  }

  load(id: string): MapDocument | null {
    return this.read()[id] ?? null;
  }

  /** Save by name, returning the id the draft is stored under. */
  save(map: MapDocument): string {
    const id = slugify(map.name);
    const drafts = this.read();
    drafts[id] = map;
    this.write(drafts);
    const times = this.readTimes();
    times[id] = Date.now();
    this.writeTimes(times);
    return id;
  }

  remove(id: string): void {
    const drafts = this.read();
    delete drafts[id];
    this.write(drafts);
    const times = this.readTimes();
    delete times[id];
    this.writeTimes(times);
  }

  /** Park a map for the reload that starts a test run or the 3D builder. */
  stage(map: MapDocument): void {
    try {
      this.session?.setItem(SESSION_KEY, JSON.stringify(map));
    } catch {
      // Without session storage a test run falls back to the empty template.
    }
  }

  /** The map parked for this boot, if any. */
  staged(): MapDocument | null {
    try {
      const raw = this.session?.getItem(SESSION_KEY);
      return raw ? parseMap(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private readTimes(): Record<string, number> {
    try {
      const raw = this.storage?.getItem(`${STORAGE_KEY}.times`);
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      return {};
    }
  }

  private writeTimes(times: Record<string, number>): void {
    try {
      this.storage?.setItem(`${STORAGE_KEY}.times`, JSON.stringify(times));
    } catch {
      // As above: the draft is still there, it just sorts last.
    }
  }
}

/** A blank map, ready to be filled in by the editor. */
export function blankMap(): MapDocument {
  return createMap('Untitled Deck');
}
